import {useState, useEffect, useMemo} from 'react';
import {Sheet, SheetContent, SheetHeader, SheetTitle} from '@common-web/components/ui/sheet';
import {Button} from '@common-web/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {Textarea} from '@common-web/components/ui/textarea';
import {Label} from '@common-web/components/ui/label';
import {Badge} from '@common-web/components/ui/badge';
import {Input} from '@common-web/components/ui/input';
import {isDryRunStepKey} from '@common/onboarding/isDryRunStepKey';
import {trpc} from '@common-web/lib/trpc';
import {useToast} from '@common-web/hooks/use-toast';
import {useNavigate} from 'react-router-dom';
import {Building2} from 'lucide-react';
import type {StepStatus} from './ChecklistStepRow';
import {StepActionSetupPlans} from './StepActionSetupPlans';
import {StepActionSetCommissionAgents} from './StepActionSetCommissionAgents';

interface StepDetailSheetProps {
  stepId: string | null;
  onboardingId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const statusOptions: Array<{value: StepStatus; label: string}> = [
  {value: 'NOT_STARTED', label: 'Not Started'},
  {value: 'IN_PROGRESS', label: 'In Progress'},
  {value: 'BLOCKED', label: 'Blocked'},
  {value: 'SKIPPED', label: 'Skipped'},
];

// Step keys that have dedicated action panels
const ACTION_STEP_KEYS = new Set(['create_group', 'setup_plans', 'set_commission_agents']);

export function StepDetailSheet({stepId, onboardingId, open, onOpenChange}: StepDetailSheetProps) {
  const [newNote, setNewNote] = useState('');
  const {toast} = useToast();
  const navigate = useNavigate();

  const utils = trpc.useUtils();

  const onboardingQuery = trpc.internal.onboarding.getOnboarding.useQuery(
    {onboardingId},
    {enabled: open && !!stepId},
  );

  const usersQuery = trpc.internal.userManagement.listUsers.useQuery({archived: false});

  const internalUsers = useMemo(
    () =>
      (usersQuery.data?.users ?? []).filter(
        (u: {id: string; name: string; email: string; isInternal?: boolean}) =>
          u.isInternal === true,
      ),
    [usersQuery.data],
  );

  const step = useMemo(() => {
    if (!stepId || !onboardingQuery.data) return null;
    const steps = onboardingQuery.data.steps as Array<{
      id: string;
      stepKey: string;
      label: string;
      phaseGroup: string;
      order: number;
      isRequired: boolean;
      status: StepStatus;
      slaTargetDays: number;
      assignee: {id: string; name: string} | null;
      completedBy: {id: string; name: string} | null;
      completedAt: string | Date | null;
      startedAt: string | Date | null;
      notes: string | null;
      assigneeId: string | null;
      dryRunFilesProcessed: number | null;
      dryRunErrorsFound: number | null;
      dryRunOutcome: 'PASS' | 'FAIL' | null;
    }>;
    return steps.find((s) => s.id === stepId) ?? null;
  }, [stepId, onboardingQuery.data]);

  const hasDryRunValidationReport = useMemo(() => {
    if (!step || !isDryRunStepKey(step.stepKey)) return true;
    return (
      step.dryRunFilesProcessed != null &&
      step.dryRunErrorsFound != null &&
      step.dryRunOutcome != null
    );
  }, [step]);

  const [selectedStatus, setSelectedStatus] = useState<StepStatus>('NOT_STARTED');
  const [selectedAssigneeId, setSelectedAssigneeId] = useState<string>('unassigned');
  const [dryRunFiles, setDryRunFiles] = useState('');
  const [dryRunErrors, setDryRunErrors] = useState('');
  const [dryRunOutcomeSel, setDryRunOutcomeSel] = useState<'PASS' | 'FAIL' | 'unset'>('unset');

  useEffect(() => {
    if (step) {
      setSelectedStatus(step.status);
      setSelectedAssigneeId(step.assigneeId ?? 'unassigned');
      setNewNote('');
      setDryRunFiles(
        step.dryRunFilesProcessed != null && !Number.isNaN(step.dryRunFilesProcessed)
          ? String(step.dryRunFilesProcessed)
          : '',
      );
      setDryRunErrors(
        step.dryRunErrorsFound != null && !Number.isNaN(step.dryRunErrorsFound)
          ? String(step.dryRunErrorsFound)
          : '',
      );
      setDryRunOutcomeSel(
        step.dryRunOutcome === 'PASS' || step.dryRunOutcome === 'FAIL'
          ? step.dryRunOutcome
          : 'unset',
      );
    }
  }, [step]);

  const updateStepMutation = trpc.internal.onboarding.updateStep.useMutation({
    onSuccess: () => {
      utils.internal.onboarding.getOnboarding.invalidate({onboardingId});
      void utils.internal.onboarding.listOnboardings.invalidate();
    },
  });

  const completeStepMutation = trpc.internal.onboarding.completeStep.useMutation({
    onSuccess: () => {
      utils.internal.onboarding.getOnboarding.invalidate({onboardingId});
      void utils.internal.onboarding.listOnboardings.invalidate();
    },
  });

  const addStepNoteMutation = trpc.internal.onboarding.addStepNote.useMutation({
    onSuccess: () => {
      utils.internal.onboarding.getOnboarding.invalidate({onboardingId});
      setNewNote('');
    },
  });

  function handleStatusChange(value: string) {
    const status = value as StepStatus;
    setSelectedStatus(status);
    if (step) {
      updateStepMutation.mutate({
        stepId: step.id,
        status,
      });
    }
  }

  function handleAssigneeChange(value: string) {
    setSelectedAssigneeId(value);
    if (step) {
      updateStepMutation.mutate({
        stepId: step.id,
        assigneeId: value === 'unassigned' ? undefined : value,
      });
    }
  }

  function handleComplete() {
    if (step) {
      completeStepMutation.mutate({
        stepId: step.id,
      });
    }
  }

  function handleAddNote() {
    if (!step || !newNote.trim()) return;
    addStepNoteMutation.mutate({
      stepId: step.id,
      note: newNote.trim(),
    });
  }

  function handleSaveDryRunReport() {
    if (!step || !isDryRunStepKey(step.stepKey)) return;
    const filesParsed = dryRunFiles.trim() === '' ? null : parseInt(dryRunFiles, 10);
    const errorsParsed = dryRunErrors.trim() === '' ? null : parseInt(dryRunErrors, 10);
    if (filesParsed !== null && Number.isNaN(filesParsed)) return;
    if (errorsParsed !== null && Number.isNaN(errorsParsed)) return;
    if (filesParsed !== null && filesParsed < 0) return;
    if (errorsParsed !== null && errorsParsed < 0) return;

    updateStepMutation.mutate(
      {
        stepId: step.id,
        dryRunFilesProcessed: filesParsed,
        dryRunErrorsFound: errorsParsed,
        dryRunOutcome: dryRunOutcomeSel === 'unset' ? null : dryRunOutcomeSel,
      },
      {
        onSuccess: () => {
          toast({
            title: 'Validation report saved',
            description: 'Dry run counts and outcome have been updated for this step.',
          });
        },
      },
    );
  }

  function handleActionComplete() {
    utils.internal.onboarding.getOnboarding.invalidate({onboardingId});
  }

  const hasActionPanel = step && ACTION_STEP_KEYS.has(step.stepKey);
  const isCompleted = step?.status === 'COMPLETED';

  // Extract data needed for action panels
  const onboarding = onboardingQuery.data;
  const rfcSubmission = onboarding?.rfcSubmission as {
    id: string;
    companyName: string | null;
    section125FormData: unknown;
    mecFormData: unknown;
  } | null;
  const clientId = onboarding?.clientId ?? '';
  const groupId = onboarding?.group
    ? (onboarding.group as {id: string; name: string; vbaGroupId: string | null}).vbaGroupId
    : null;

  if (!step && open) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Loading...</SheetTitle>
          </SheetHeader>
          <div className='py-6 text-sm text-gray-500'>Loading step details...</div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className='overflow-y-auto sm:max-w-lg'>
        <SheetHeader>
          <div className='flex items-center gap-2'>
            <SheetTitle>{step?.label ?? 'Step Detail'}</SheetTitle>
            {isCompleted && (
              <Badge className='bg-emerald-100 text-emerald-700 text-xs'>Completed</Badge>
            )}
          </div>
        </SheetHeader>

        <div className='mt-6 space-y-6'>
          {/* Action Panel for special steps */}
          {hasActionPanel && !isCompleted && (
            <div className='border rounded-lg p-4 bg-gray-50/50'>
              <p className='text-xs font-medium text-gray-500 uppercase tracking-wider mb-3'>
                Step Action
              </p>
              {step.stepKey === 'create_group' && (
                <div className='space-y-2'>
                  <p className='text-xs text-muted-foreground'>
                    Create this group in VBA using the build wizard.
                  </p>
                  <Button
                    className='w-full'
                    onClick={() =>
                      navigate(
                        `/groups/new?onboardingId=${onboardingId}&stepId=${step.id}`,
                      )
                    }
                  >
                    <Building2 className='h-4 w-4 mr-2' />
                    Build Group
                  </Button>
                </div>
              )}
              {step.stepKey === 'setup_plans' && (
                <StepActionSetupPlans
                  onboardingId={onboardingId}
                  stepId={step.id}
                  clientId={clientId}
                  groupId={groupId}
                  onComplete={handleActionComplete}
                />
              )}
              {step.stepKey === 'set_commission_agents' && (
                <StepActionSetCommissionAgents
                  onboardingId={onboardingId}
                  stepId={step.id}
                  clientId={clientId}
                  groupId={groupId}
                  onComplete={handleActionComplete}
                />
              )}
            </div>
          )}

          {/* Dry run validation report */}
          {step && isDryRunStepKey(step.stepKey) && (
            <div className='space-y-3 rounded-lg border border-amber-200 bg-amber-50/50 p-4'>
              <p className='text-xs font-medium text-amber-900 uppercase tracking-wider'>
                Validation report
              </p>
              <div className='grid grid-cols-2 gap-3'>
                <div className='space-y-1.5'>
                  <Label className='text-xs'>Files processed</Label>
                  <Input
                    type='number'
                    min={0}
                    className='h-9'
                    placeholder='—'
                    value={dryRunFiles}
                    onChange={(e) => setDryRunFiles(e.target.value)}
                  />
                </div>
                <div className='space-y-1.5'>
                  <Label className='text-xs'>Errors found</Label>
                  <Input
                    type='number'
                    min={0}
                    className='h-9'
                    placeholder='—'
                    value={dryRunErrors}
                    onChange={(e) => setDryRunErrors(e.target.value)}
                  />
                </div>
              </div>
              <div className='space-y-1.5'>
                <Label className='text-xs'>Pass / fail</Label>
                <Select
                  value={dryRunOutcomeSel}
                  onValueChange={(v) => setDryRunOutcomeSel(v as 'PASS' | 'FAIL' | 'unset')}
                >
                  <SelectTrigger className='h-9'>
                    <SelectValue placeholder='Select outcome' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='unset'>Not set</SelectItem>
                    <SelectItem value='PASS'>Pass</SelectItem>
                    <SelectItem value='FAIL'>Fail</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                type='button'
                variant='secondary'
                size='sm'
                className='bg-brand-blue hover:bg-brand-blue-dark text-white font-medium'
                onClick={handleSaveDryRunReport}
                disabled={updateStepMutation.isPending}
              >
                {updateStepMutation.isPending ? 'Saving...' : 'Save validation report'}
              </Button>
            </div>
          )}

          {/* Status */}
          <div className='space-y-2'>
            <Label>Status</Label>
            <Select value={selectedStatus} onValueChange={handleStatusChange}>
              <SelectTrigger>
                <SelectValue placeholder='Select status' />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Assignee */}
          <div className='space-y-2'>
            <Label>Assignee</Label>
            <Select value={selectedAssigneeId} onValueChange={handleAssigneeChange}>
              <SelectTrigger>
                <SelectValue placeholder='Select assignee' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='unassigned'>Unassigned</SelectItem>
                {internalUsers.map((user: {id: string; name: string; email: string}) => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div className='space-y-2'>
            <Label>Notes</Label>
            {step?.notes && (
              <div className='rounded-md bg-gray-50 p-3 text-sm text-gray-700 whitespace-pre-wrap'>
                {step.notes}
              </div>
            )}
            <Textarea
              placeholder='Add a note...'
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              rows={3}
            />
            <Button
              variant='outline'
              size='sm'
              onClick={handleAddNote}
              disabled={!newNote.trim() || addStepNoteMutation.isPending}
            >
              {addStepNoteMutation.isPending ? 'Adding...' : 'Add Note'}
            </Button>
          </div>

          {/* Mark Complete (only for non-action steps or as fallback) */}
          {step && !isCompleted && !hasActionPanel && (
            <div className='space-y-2'>
              <Button
                className='w-full bg-emerald-600 hover:bg-emerald-700 text-white'
                onClick={handleComplete}
                disabled={
                  completeStepMutation.isPending ||
                  (isDryRunStepKey(step.stepKey) && !hasDryRunValidationReport)
                }
              >
                {completeStepMutation.isPending ? 'Completing...' : 'Mark Complete'}
              </Button>
              {isDryRunStepKey(step.stepKey) && !hasDryRunValidationReport && (
                <p className='text-xs text-amber-800'>
                  Save a validation report above before marking this step complete.
                </p>
              )}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
